import { z } from 'zod';
import { AWARD_VISIBILITIES, ROLES } from '@bulk/types';
import { dateOnlySchema, idSchema, patchOf, publicAssetSchema } from './common';

export const KUDOS_MAX_LENGTH = 280;
export const KUDOS_FEED_MAX = 100;
export const PUBLIC_AWARDS_MAX = 24;

const AWARD_MONTH_PATTERN = /^[0-9]{4}-(0[1-9]|1[0-2])$/;
export const awardMonthSchema = z.string().regex(AWARD_MONTH_PATTERN, 'A month looks like 2026-10');

const localizedTitles = z.record(z.string(), z.string().trim().max(120));
const localizedReasons = z.record(z.string(), z.string().trim().max(KUDOS_MAX_LENGTH));

const awardFields = z.object({
  employeeId: idSchema,
  title: z.string().trim().min(1).max(120),
  titleLocalized: localizedTitles.default({}),
  month: awardMonthSchema.nullable().optional().describe('The month the award is for (2026-10); exactly one of this and awardedOn'),
  awardedOn: dateOnlySchema.nullable().optional().describe('Or the day it is for'),
  reason: z.string().trim().max(KUDOS_MAX_LENGTH).default(''),
  reasonLocalized: localizedReasons.default({}),
  visibility: z.enum(AWARD_VISIBILITIES).default('STAFF').describe('PUBLIC awards are shown on the gym site; STAFF awards never leave the staff area'),
});

export const createAwardSchema = awardFields.refine((input) => (input.month ? 1 : 0) + (input.awardedOn ? 1 : 0) === 1, { message: 'Give a month or a day, not both', path: ['month'] });
export type CreateAwardInput = z.infer<typeof createAwardSchema>;

export const updateAwardSchema = patchOf(awardFields.omit({ employeeId: true }));
export type UpdateAwardInput = z.infer<typeof updateAwardSchema>;

export const awardListQuerySchema = z.object({
  visibility: z.enum(AWARD_VISIBILITIES).optional(),
  employeeId: idSchema.optional(),
  trainerId: idSchema.optional().describe('Only the awards of the employee linked to this trainer'),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});
export type AwardListQuery = z.infer<typeof awardListQuerySchema>;

export const awardSchema = z.object({
  id: idSchema,
  employeeId: idSchema,
  employeeName: z.string(),
  trainerId: idSchema.nullable().describe('The trainer this employee is, which shows a public award on their page'),
  title: z.string(),
  titleLocalized: z.record(z.string(), z.string()),
  month: z.string().nullable(),
  awardedOn: z.string().nullable(),
  reason: z.string(),
  reasonLocalized: z.record(z.string(), z.string()),
  visibility: z.enum(AWARD_VISIBILITIES),
  pictureUrl: z.string().nullable().describe('A public address for a PUBLIC award; a signed link that expires after a few minutes for a STAFF one'),
  createdAt: z.string(),
});
export type AwardDto = z.infer<typeof awardSchema>;

export const awardCandidateSchema = z.object({
  id: idSchema,
  name: z.string(),
  position: z.string().nullable(),
  trainerId: idSchema.nullable(),
});
export type AwardCandidateDto = z.infer<typeof awardCandidateSchema>;

export const sendKudoSchema = z.object({
  toGymUserId: idSchema.describe('A colleague from `GET /recognition/kudos/recipients`'),
  message: z.string().trim().min(1).max(KUDOS_MAX_LENGTH),
});
export type SendKudoInput = z.infer<typeof sendKudoSchema>;

export const kudosListQuerySchema = z.object({ limit: z.coerce.number().int().min(1).max(KUDOS_FEED_MAX).default(30) });
export type KudosListQuery = z.infer<typeof kudosListQuerySchema>;

const kudoPersonSchema = z.object({ gymUserId: idSchema, name: z.string() });

export const kudoSchema = z.object({
  id: idSchema,
  from: kudoPersonSchema,
  to: kudoPersonSchema,
  message: z.string(),
  createdAt: z.string(),
  hiddenAt: z.string().nullable().describe('Set only for people who manage recognition; everyone else never receives a hidden kudo'),
  hiddenBy: z.string().nullable(),
  mine: z.boolean().describe('The caller sent it'),
  forMe: z.boolean().describe('The caller received it'),
});
export type KudoDto = z.infer<typeof kudoSchema>;

export const kudoRecipientSchema = z.object({
  gymUserId: idSchema,
  name: z.string(),
  role: z.enum(ROLES),
});
export type KudoRecipientDto = z.infer<typeof kudoRecipientSchema>;

export const birthdayColleagueSchema = z.object({
  gymUserId: idSchema,
  name: z.string(),
});
export type BirthdayColleagueDto = z.infer<typeof birthdayColleagueSchema>;

export const publicAwardSchema = z.object({
  id: idSchema,
  name: z.string().describe('The employee’s name; in the trainer’s own spelling per locale when they are a trainer'),
  nameLocalized: z.record(z.string(), z.string()),
  trainerId: idSchema.nullable(),
  title: z.string(),
  titleLocalized: z.record(z.string(), z.string()),
  reason: z.string(),
  reasonLocalized: z.record(z.string(), z.string()),
  month: z.string().nullable(),
  awardedOn: z.string().nullable(),
  picture: publicAssetSchema.nullable().describe('The award’s picture, else the trainer’s website photo'),
});
export type PublicAwardDto = z.infer<typeof publicAwardSchema>;
